import { Component, computed, inject, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanyEmissionsWasteService } from '../../../../../services/companies/company-emissions-waste.service';
import { CompanyEmissionsWasteDto, UpsertEmissionsWasteRequest } from '../../../../../models/api/companyEmissionsWasteDto';
import { CsrMeasurementUnit } from '../../../../../models/csrMeasurementUnit';
import { Currency } from '../../../../../models/currency';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

interface EmWRow {
  key: string;
  label: string;
  showPct?: boolean;
  pctBase?: string;
  noUnit?: boolean;
  useCurrency?: boolean;
  sectionHeader?: string;
  isPct?: boolean;
  indent?: boolean;
}

@Component({
  selector: 'app-emissions-waste-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './emissions-waste-tab.component.html',
  styleUrl: './emissions-waste-tab.component.scss'
})
export class EmissionsWasteTabComponent implements OnInit, OnChanges, OnDestroy, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private emissionsWasteService = inject(CompanyEmissionsWasteService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);
  private destroy$ = new Subject<void>();
  private pctLastEdited: Record<string, 'num' | 'pct'> = {};
  private readonly parentChildMap: { parent: string; children: string[] }[] = [
    { parent: 'wasteDivertedFromDisposal', children: ['prepForReuse', 'recycling', 'otherRecoveryOperations'] },
    { parent: 'wasteDirectedToDisposal', children: ['incinerationWithEnergyRecovery', 'incinerationWithoutEnergyRecovery', 'landfill', 'otherDisposalOperations'] },
    { parent: 'scope3', children: ['purchasedGoods', 'capitalGoods', 'fuelAndEnergyRelated', 'upstreamTransportation', 'wasteGenerated', 'businessTravel', 'employeeCommuting', 'upstreamLeasedAssets', 'downstreamTransportation', 'processingOfSoldProducts', 'useOfSoldProducts', 'endOfLifeTreatment', 'downstreamLeasedAssets', 'franchises', 'investments', 'upstreamScope3', 'downstreamScope3'] },
    { parent: 'scope1AndScope2', children: ['scope1', 'scope2Market'] },
    { parent: 'scope1AndScope2AndScope3', children: ['scope1', 'scope2Market', 'scope3'] },
  ];
  private readonly triads: { total: string; parts: [string, string] }[] = [
    { total: 'wasteTotal', parts: ['nonHazardousWaste', 'hazardousWaste'] },
    { total: 'wasteTotal', parts: ['wasteDivertedFromDisposal', 'wasteDirectedToDisposal'] },
  ];

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.EmissionsWaste));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  isCarriedForward = false;
  showKeywords = true;
  otherMeasurement: string = '';
  otherData: CompanyEmissionsWasteDto | null = null;

  allUnits: CsrMeasurementUnit[] = [];
  emissionsUnits: CsrMeasurementUnit[] = [];
  greenhouseGasUnits: CsrMeasurementUnit[] = [];
  wasteUnits: CsrMeasurementUnit[] = [];
  currencies: Currency[] = [];

  emissionsTopRows: EmWRow[] = [
    { key: 'scope1', label: 'Scope 1' },
    { key: 'scope2Location', label: 'Scope 2 - Location' },
    { key: 'scope2Market', label: 'Scope 2 - Market' },
    { key: 'scope2', label: 'Scope 2' },
    { key: 'scope1AndScope2', label: 'Scope (1+2)' },
    { key: 'scope3', label: 'Scope 3' },
  ];

  scope3DetailRows: EmWRow[] = [
    { key: 'purchasedGoods', label: 'Purchased goods and services' },
    { key: 'capitalGoods', label: 'Capital goods' },
    { key: 'fuelAndEnergyRelated', label: 'Fuel- and energy-related activities' },
    { key: 'upstreamTransportation', label: 'Upstream transportation and distribution' },
    { key: 'wasteGenerated', label: 'Waste generated in operations' },
    { key: 'businessTravel', label: 'Business travel' },
    { key: 'employeeCommuting', label: 'Employee commuting' },
    { key: 'upstreamLeasedAssets', label: 'Upstream leased assets' },
    { key: 'downstreamTransportation', label: 'Downstream transportation and distribution' },
    { key: 'processingOfSoldProducts', label: 'Processing of sold products' },
    { key: 'useOfSoldProducts', label: 'Use of sold products' },
    { key: 'endOfLifeTreatment', label: 'End-of-life treatment of sold products' },
    { key: 'downstreamLeasedAssets', label: 'Downstream leased assets' },
    { key: 'franchises', label: 'Franchises' },
    { key: 'investments', label: 'Investments' },
    { key: 'upstreamScope3', label: 'Upstream scope 3 emissions Other' },
    { key: 'downstreamScope3', label: 'Downstream scope 3 emissions Other' },
  ];

  emissionsBottomRows: EmWRow[] = [
    { key: 'scope1AndScope2AndScope3', label: 'Scope (1+2+3)' },
    { key: 'greenhouseGas', label: 'Greenhouse gas (GHG) emissions intensity' },
    { key: 'flaringGases', label: 'Flaring Gases' },
    { key: 'cementCo2', label: 'Cement CO2' },
    { key: 'ozaneDepletingSubstances', label: 'Ozone Depleting Substances' },
    { key: 'noxEmissions', label: 'NOx Emissions' },
    { key: 'soxEmissions', label: 'SOx Emissions' },
    { key: 'vocEmissions', label: 'VOC Emissions' },
    { key: 'waterPollutantEmissions', label: 'Water Pollutant Emissions' },
    { key: 'emissionReduction', label: 'Emission Reduction' },
    { key: 'internalCarbonPricing', label: 'Internal Carbon Pricing' },
  ];

  wasteRows: EmWRow[] = [
    { key: 'wasteTotal', label: 'Waste Total', showPct: true, pctBase: 'wasteTotal' },
    { key: 'nonHazardousWaste', label: 'Non-Hazardous Waste', showPct: true, pctBase: 'wasteTotal' },
    { key: 'hazardousWaste', label: 'Hazardous Waste', showPct: true, pctBase: 'wasteTotal' },
    { key: 'wasteDivertedFromDisposal', label: 'Waste Diverted from Disposal', showPct: true, pctBase: 'wasteTotal' },
    { key: 'prepForReuse', label: 'Prep for Reuse', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'recycling', label: 'Recycling', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'otherRecoveryOperations', label: 'Other Recovery Operations', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'wasteDirectedToDisposal', label: 'Waste Directed to Disposal', showPct: true, pctBase: 'wasteTotal' },
    { key: 'incinerationWithEnergyRecovery', label: 'Incineration w/ Energy Recovery', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'incinerationWithoutEnergyRecovery', label: 'Incineration w/o Energy Recovery', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'landfill', label: 'Landfill', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'otherDisposalOperations', label: 'Other Disposal Operations', showPct: true, pctBase: 'wasteTotal', indent: true },
    { key: 'materialsRecycledAndReusedTotal', label: 'Materials Recycled & Reused Total', noUnit: true },
    { key: 'materialsTotal', label: 'Materials Total' },
  ];

  environmentalRows: EmWRow[] = [
    { key: 'environmentalExpenditures', label: 'Environmental Expenditures', useCurrency: true },
    { key: 'environmentalNonCompliance', label: 'Environmental Non-Compliance', useCurrency: true },
  ];

  private get allEmissionsRows(): EmWRow[] {
    return [...this.emissionsTopRows, ...this.scope3DetailRows, ...this.emissionsBottomRows];
  }

  form: FormGroup = this.buildForm();

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  private readonly keywordMapping: { formField: string; type: KeywordType }[] = [
    { formField: 'emission', type: KeywordType.Emission },
    { formField: 'waste', type: KeywordType.Waste },
    { formField: 'environmentalCompliance', type: KeywordType.EnvironmentalCompliance },
  ];

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('emissionsWaste'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'emissionsWaste', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'emissionsWaste', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.setupPctAutoCalc();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private patchKeywords(): void {
    const patch: Record<string, string> = {};
    for (const mapping of this.keywordMapping) {
      const kw = this.keywords.find(k => k.type === mapping.type);
      patch[mapping.formField] = kw?.value ?? '';
    }
    this.form.get('keywords')!.patchValue(patch, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasEwChanges = this.form.dirty && !hasKeywordChanges;
    if (hasEwChanges && hasKeywordChanges) return 'emissions & waste data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'emissions & waste data';
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  formatNumber(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  }

  getPct(key: string, baseKey: string): string {
    const value = this.form.get(key)?.value;
    const base = this.form.get(baseKey)?.value;
    if (key === baseKey && value != null) return '100';
    if (value == null || base == null || base === 0) return '';
    const pct = Math.round((value / base * 100) * 100) / 100;
    return pct.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  getConverted(key: string): number | null {
    const value = this.form.get(key)?.value;
    const unitId = this.form.get(key + 'UnitId')?.value;
    if (value == null || !unitId) return null;
    const unit = this.allUnits.find(u => u.id === unitId);
    if (unit?.converter == null) return null;
    return value * unit.converter;
  }

  getConvertedRestatement(key: string): number | null {
    const value = this.form.get(key + 'Restatement')?.value;
    const unitId = this.form.get(key + 'UnitId')?.value;
    if (value == null || !unitId) return null;
    const unit = this.allUnits.find(u => u.id === unitId);
    if (unit?.converter == null) return null;
    return value * unit.converter;
  }

  /**
   * The Other Measurement's converted figure: that year's value in that year's unit. The
   * comparison cells used to repeat the current year's conversion instead.
   */
  getOtherConverted(key: string, suffix: '' | 'Restatement' = ''): number | null {
    const value = this.getOtherValue(key + suffix);
    const unitId = this.getOtherStringValue(key + 'UnitId');
    if (value == null || !unitId) return null;
    const unit = this.allUnits.find(u => u.id === unitId);
    if (unit?.converter == null) return null;
    return value * unit.converter;
  }

  getUnitName(unitId: string | null | undefined): string {
    if (!unitId) return '';
    return this.allUnits.find(u => u.id === unitId)?.name ?? '';
  }

  getCurrencyName(currencyId: string | null | undefined): string {
    if (!currencyId) return '';
    return this.currencies.find(c => c.id === currencyId)?.name ?? '';
  }

  getOtherValue(key: string): number | null | undefined {
    if (!this.otherData) return undefined;
    return (this.otherData as any)[key];
  }

  getOtherStringValue(key: string): string | null | undefined {
    if (!this.otherData) return undefined;
    return (this.otherData as any)[key];
  }

  private buildForm(): FormGroup {
    const controls: Record<string, any> = {};
    for (const row of [...this.allEmissionsRows, ...this.wasteRows, ...this.environmentalRows]) {
      controls[row.key] = [null as number | null];
      controls[row.key + 'Restatement'] = [null as number | null];
      if (row.noUnit) {
        // No unit control
      } else if (row.useCurrency) {
        controls[row.key + 'CurrencyId'] = [''];
      } else {
        controls[row.key + 'UnitId'] = [''];
      }
      if (row.showPct) {
        controls[row.key + 'Pct'] = [null as number | null];
      }
    }
    controls['comments'] = [''];
    controls['keywords'] = this.fb.group({
      emission: [''],
      waste: [''],
      environmentalCompliance: ['']
    });
    return this.fb.group(controls);
  }

  private calcPct(numerator: number | null, denominator: number | null): number | null {
    if (numerator == null || denominator == null || denominator === 0) return null;
    return Math.round((numerator * 100 / denominator) * 100) / 100;
  }

  private setupPctAutoCalc(): void {
    const pctRows = this.wasteRows.filter(r => r.showPct);
    const baseKeys = [...new Set(pctRows.map(r => r.pctBase!))];
    const ctrl = (name: string) => this.form.get(name)!;

    for (const row of pctRows) {
      if (row.key === row.pctBase) continue;

      ctrl(row.key).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.pctLastEdited[row.key] = 'num';
        this.syncField(row.key, row.pctBase!, 'num');
      });

      ctrl(row.key + 'Pct').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.pctLastEdited[row.key] = 'pct';
        this.syncField(row.key, row.pctBase!, 'pct');
      });
    }

    for (const baseKey of baseKeys) {
      ctrl(baseKey).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        const dependentRows = pctRows.filter(r => r.pctBase === baseKey && r.key !== baseKey);
        for (const row of dependentRows) {
          const side = this.pctLastEdited[row.key] === 'pct' ? 'pct' : 'num';
          this.syncField(row.key, baseKey, side);
        }
        const basePctCtrl = this.form.get(baseKey + 'Pct');
        if (basePctCtrl) {
          const baseVal = ctrl(baseKey).value;
          basePctCtrl.setValue(baseVal != null ? 100 : null, { emitEvent: false });
        }
      });
    }

    for (const mapping of this.parentChildMap) {
      for (const child of mapping.children) {
        ctrl(child).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.sumIntoParent(mapping.parent, mapping.children);
        });
        ctrl(child + 'Restatement').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.sumIntoParent(mapping.parent, mapping.children, 'Restatement');
        });
      }
    }

    const triadFields = new Set<string>(this.triads.flatMap(t => [t.total, ...t.parts]));
    for (const field of triadFields) {
      ctrl(field).valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: number | null) => {
        this.applyTriad(field, val);
      });
    }
  }

  private sumIntoParent(parentKey: string, childKeys: string[], suffix: '' | 'Restatement' = ''): void {
    const values = childKeys.map(k => this.form.get(k + suffix)!.value as number | null);
    const nums = values.filter((v): v is number => v != null);
    const sum = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) : null;
    this.form.get(parentKey + suffix)!.setValue(sum, { emitEvent: true });
  }

  private applyTriad(changedField: string, newValue: number | null): void {
    if (newValue == null) return;
    for (const triad of this.triads) {
      if (changedField !== triad.total && !triad.parts.includes(changedField)) continue;
      const t = this.form.get(triad.total)!.value as number | null;
      const a = this.form.get(triad.parts[0])!.value as number | null;
      const b = this.form.get(triad.parts[1])!.value as number | null;
      const nonNullCount = [t, a, b].filter(v => v != null).length;
      if (nonNullCount !== 2) continue;
      if (t == null) {
        this.form.get(triad.total)!.setValue(a! + b!, { emitEvent: true });
      } else if (a == null) {
        this.form.get(triad.parts[0])!.setValue(Math.max(0, t - b!), { emitEvent: true });
      } else {
        this.form.get(triad.parts[1])!.setValue(Math.max(0, t - a!), { emitEvent: true });
      }
    }
  }

  private syncField(numField: string, baseField: string, side: 'num' | 'pct'): void {
    const pctField = numField + 'Pct';
    const base = this.form.get(baseField)!.value as number | null;
    const set = (name: string, value: number | null) =>
      this.form.get(name)!.setValue(value, { emitEvent: false });

    if (side === 'pct') {
      const pct = this.form.get(pctField)!.value as number | null;
      if (base != null && pct != null) {
        set(numField, Math.round(base * pct / 100 * 100) / 100);
      }
      for (const mapping of this.parentChildMap) {
        if (mapping.children.includes(numField)) {
          this.sumIntoParent(mapping.parent, mapping.children);
        }
      }
    } else {
      const num = this.form.get(numField)!.value as number | null;
      set(pctField, this.calcPct(num, base));
    }
  }

  private recalcAllPct(): void {
    for (const row of this.wasteRows) {
      if (!row.showPct) continue;
      const pctControl = this.form.get(row.key + 'Pct');
      if (!pctControl) continue;
      if (pctControl.value != null) {
        this.pctLastEdited[row.key] = 'pct';
        continue;
      }
      if (row.key === row.pctBase) {
        const baseVal = this.form.get(row.key)!.value;
        pctControl.setValue(baseVal != null ? 100 : null, { emitEvent: false });
      } else {
        const num = this.form.get(row.key)!.value as number | null;
        const base = this.form.get(row.pctBase!)!.value as number | null;
        pctControl.setValue(this.calcPct(num, base), { emitEvent: false });
      }
    }
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'emissionsWaste', year }));
    this.loadData();
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.emissionsWasteService.get(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private performSave(): void {
    if (!this.companyId) return;

    const request = this.buildRequest();

    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, this.keywordMapping, this.keywords);

    const hasEwData = this.isCarriedForward || Object.keys(this.form.value).some(k => k !== 'keywords' && this.form.get(k)?.dirty);
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => this.keywordMapping.some(m => m.type === k.type)
    );

    if (!hasEwData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasEwData ? this.emissionsWasteService.upsert(this.companyId!, request) : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_ewResult, keywordsResult]) => {
        // The year has a record of its own now. Left set, the flag kept Save enabled after the
        // save went through, until the tab was reloaded.
        this.isCarriedForward = false;
        this.showYearWarning = false;
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private buildRequest(): UpsertEmissionsWasteRequest {
    const v = this.form.value;
    const request: any = {
      year: parseInt(this.selectedYear),
      comments: v.comments || null
    };
    for (const row of [...this.allEmissionsRows, ...this.wasteRows, ...this.environmentalRows]) {
      request[row.key] = v[row.key] ?? null;
      request[row.key + 'Restatement'] = v[row.key + 'Restatement'] ?? null;
      if (row.noUnit) {
        // No unit field
      } else if (row.useCurrency) {
        request[row.key + 'CurrencyId'] = v[row.key + 'CurrencyId'] || null;
      } else {
        request[row.key + 'UnitId'] = v[row.key + 'UnitId'] || null;
      }
      if (row.showPct) {
        request[row.key + 'Pct'] = v[row.key + 'Pct'] ?? null;
      }
    }
    return request as UpsertEmissionsWasteRequest;
  }

  private loadLookupData(): void {
    this.spinnerService.show();

    forkJoin({
      units: this.lutService.getCsrMeasurementUnits(),
      currencies: this.lutService.getCurrencies()
    }).subscribe({
      next: ({ units, currencies }) => {
        this.allUnits = units.sort((a, b) => a.name.localeCompare(b.name));
        // Type 5 = Emissions, Type 1 = Other (Greenhouse Gas), Type 3 = Waste
        this.emissionsUnits = this.allUnits.filter(u => u.type === 5);
        this.greenhouseGasUnits = this.allUnits.filter(u => u.type === 1);
        this.wasteUnits = this.allUnits.filter(u => u.type === 3);
        this.currencies = currencies.sort((a, b) => a.name.localeCompare(b.name));
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();
    this.emissionsWasteService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isCarriedForward || resource.state === 'absent';
        if (formValues) {
          this.patchForm(formValues);
        } else {
          // Nothing saved for the year: the amounts are still quoted in the company's
          // country currency, which the API hands over beside the empty envelope.
          this.resetForm(resource.context?.defaultCurrencyId ?? '');
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        this.isCarriedForward = false;
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading emissions & waste data');
        }

        console.error('Emissions & waste error:', error);
      }
    });
  }

  private patchForm(data: CompanyEmissionsWasteDto): void {
    const patch: Record<string, any> = {};
    for (const row of [...this.allEmissionsRows, ...this.wasteRows, ...this.environmentalRows]) {
      patch[row.key] = (data as any)[row.key];
      patch[row.key + 'Restatement'] = (data as any)[row.key + 'Restatement'];
      if (row.noUnit) {
        // No unit to patch
      } else if (row.useCurrency) {
        patch[row.key + 'CurrencyId'] = (data as any)[row.key + 'CurrencyId'] || '';
      } else {
        patch[row.key + 'UnitId'] = (data as any)[row.key + 'UnitId'] || '';
      }
      if (row.showPct) {
        patch[row.key + 'Pct'] = (data as any)[row.key + 'Pct'];
      }
    }
    patch['comments'] = data.comments || '';
    this.form.patchValue(patch, { emitEvent: false });
    this.recalcAllPct();
    this.form.markAsPristine();
  }

  /**
   * @param currencyId The currency every amount defaults to — the company's country currency,
   * or '' when there is none to fall back to.
   */
  private resetForm(currencyId: string = ''): void {
    const keywordsValue = this.form.get('keywords')!.value;
    this.form.reset();
    for (const row of [...this.allEmissionsRows, ...this.wasteRows, ...this.environmentalRows]) {
      if (row.useCurrency) {
        this.form.get(row.key + 'CurrencyId')!.setValue(currencyId, { emitEvent: false });
      }
    }
    this.form.get('keywords')!.patchValue(keywordsValue, { emitEvent: false });
    this.form.markAsPristine();
  }
}
